import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  ArrowLeft, Star, MapPin, Calendar, Trophy, Package, Gavel, Share2, User as UserIcon,
  Award, Crown, Gem, Medal, ShieldCheck,
} from 'lucide-react';
import { timeAgo } from '../utils/timeAgo';
import { ListingActions } from '../components/ListingActions';

function PublicProfile() {
  const { username } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/users/profile/${encodeURIComponent(username)}`);
        if (alive) setProfile(res.data);
      } catch (e) {
        toast.error(e.response?.status === 404 ? 'User not found' : 'Failed to load profile');
        navigate('/');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [username, navigate]);

  if (loading) {
    return (
      <div data-testid="profile-loading" className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-400 text-sm">
        Loading profile...
      </div>
    );
  }
  if (!profile) return null;

  const initials = (profile.username || '?').slice(0, 2).toUpperCase();
  const memberAgo = timeAgo(profile.member_since);
  const memberDate = profile.member_since
    ? new Date(profile.member_since).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
    : '';
  const stats = profile.stats || {};
  const isSystem = profile.is_system;
  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';

  return (
    <div className="min-h-screen bg-slate-950 text-white pb-12">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-slate-950/95 backdrop-blur border-b border-slate-800/60 px-4 py-3 flex items-center gap-3">
        <button
          data-testid="profile-back-btn"
          onClick={() => navigate(-1)}
          className="w-8 h-8 rounded-full bg-slate-800/60 flex items-center justify-center"
        >
          <ArrowLeft className="w-4 h-4 text-slate-300" />
        </button>
        <p className="text-sm font-semibold">Profile</p>
      </div>

      <div className="max-w-md mx-auto px-4 pt-5 space-y-4">
        {/* Avatar + name */}
        <div className="text-center space-y-2">
          <div
            data-testid="profile-avatar"
            className="w-20 h-20 rounded-full mx-auto bg-gradient-to-br from-orange-500 to-purple-600 flex items-center justify-center text-2xl font-black"
          >
            {initials}
          </div>
          <div>
            <h1 data-testid="profile-username" className="text-xl font-bold">@{profile.username}</h1>
            {isSystem && (
              <span className="inline-block mt-1 text-[9px] px-2 py-0.5 rounded-full bg-slate-700 text-slate-300 font-bold uppercase">
                System Account
              </span>
            )}
            {profile.bio && (
              <p data-testid="profile-bio" className="text-xs text-slate-400 mt-2 max-w-xs mx-auto">{profile.bio}</p>
            )}
          </div>
        </div>

        {/* Rating */}
        {stats.total_ratings > 0 && (
          <Card data-testid="profile-rating-card" className="bg-amber-500/5 border-amber-500/20 p-4 text-center">
            <div className="flex items-center justify-center gap-1 mb-1">
              {[1, 2, 3, 4, 5].map(n => (
                <Star
                  key={n}
                  className={`w-4 h-4 ${n <= Math.round(stats.avg_rating) ? 'text-amber-400 fill-amber-400' : 'text-slate-700'}`}
                />
              ))}
            </div>
            <p className="text-lg font-bold text-amber-300">{stats.avg_rating} / 5</p>
            <p className="text-[10px] text-slate-500">{stats.total_ratings} {stats.total_ratings === 1 ? 'rating' : 'ratings'}</p>
          </Card>
        )}

        {/* Badges */}
        {profile.badges && profile.badges.length > 0 && (
          <div data-testid="profile-badges-section">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold mb-2">Achievements</p>
            <div className="grid grid-cols-2 gap-2">
              {profile.badges.map(b => (
                <BadgeCard key={b.id} badge={b} />
              ))}
            </div>
          </div>
        )}

        {/* Stats grid */}
        <div className="grid grid-cols-3 gap-2">
          <StatTile icon={Package} label="Hosted" value={stats.games_hosted || 0} />
          <StatTile icon={Gavel} label="Auctions" value={stats.auctions_hosted || 0} />
          <StatTile icon={Trophy} label="Wins" value={stats.wins || 0} />
        </div>
        <div className="grid grid-cols-1 gap-2">
          <StatTile icon={Package} label="Successful shipments" value={stats.completed_shipments || 0} accent />
        </div>

        {/* Meta */}
        <div className="space-y-1.5">
          {profile.location && (
            <div data-testid="profile-location" className="flex items-center gap-2 text-xs text-slate-400">
              <MapPin className="w-3.5 h-3.5" /> {profile.location}
            </div>
          )}
          {memberDate && (
            <div data-testid="profile-member-since" className="flex items-center gap-2 text-xs text-slate-400">
              <Calendar className="w-3.5 h-3.5" /> Member since {memberDate} <span className="text-slate-600">· {memberAgo}</span>
            </div>
          )}
        </div>

        {/* Share button */}
        <ListingActions
          shareUrl={shareUrl}
          shareTitle={`@${profile.username} on Vitamin C Gainz`}
          hostUsername={profile.username}
          hostTelegramUsername={profile.telegram_username}
          hostUserId={profile.user_id}
          targetType="user"
          targetId={profile.user_id}
          targetName={`@${profile.username}`}
        />
      </div>
    </div>
  );
}

function StatTile({ icon: Icon, label, value, accent = false }) {
  return (
    <div
      data-testid={`profile-stat-${label.replace(/\s+/g, '-').toLowerCase()}`}
      className={`rounded-xl border p-3 ${accent
        ? 'bg-emerald-500/10 border-emerald-500/25'
        : 'bg-slate-800/40 border-slate-700/40'}`}
    >
      <div className="flex items-center gap-1.5 text-[10px] text-slate-400 uppercase tracking-wide">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <p className={`text-lg font-bold mt-0.5 ${accent ? 'text-emerald-300' : 'text-white'}`}>{value}</p>
    </div>
  );
}

const BADGE_ICONS = { trophy: Trophy, medal: Medal, gem: Gem, crown: Crown, award: Award, shield: ShieldCheck, gavel: Gavel };
const TIER_STYLES = {
  bronze: { bg: 'from-orange-500/15 to-amber-600/15', border: 'border-orange-500/40', text: 'text-orange-300', icon: 'text-orange-400' },
  silver: { bg: 'from-slate-400/15 to-slate-300/10', border: 'border-slate-400/40', text: 'text-slate-200', icon: 'text-slate-300' },
  gold: { bg: 'from-yellow-500/20 to-amber-400/15', border: 'border-yellow-500/50', text: 'text-yellow-300', icon: 'text-yellow-400' },
  verified: { bg: 'from-sky-500/15 to-cyan-400/10', border: 'border-sky-500/45', text: 'text-sky-300', icon: 'text-sky-300' },
};

function BadgeCard({ badge }) {
  const Icon = BADGE_ICONS[badge.icon] || Award;
  const style = TIER_STYLES[badge.tier] || TIER_STYLES.bronze;
  const earnedDate = badge.earned_at
    ? new Date(badge.earned_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    : '';
  return (
    <div
      data-testid={`profile-badge-${badge.id}`}
      className={`rounded-xl border ${style.border} bg-gradient-to-br ${style.bg} p-3 flex items-start gap-2.5`}
      title={badge.description}
    >
      <Icon className={`w-6 h-6 ${style.icon} flex-shrink-0 mt-0.5`} />
      <div className="min-w-0 flex-1">
        <p className={`text-xs font-bold leading-tight ${style.text}`}>{badge.name}</p>
        <p className="text-[9px] text-slate-400 leading-tight mt-0.5 line-clamp-2">{badge.description}</p>
        {earnedDate && (
          <p className="text-[8px] text-slate-500 mt-1 uppercase tracking-wide">Earned {earnedDate}</p>
        )}
      </div>
    </div>
  );
}

export default PublicProfile;
